import type { Condition } from '@databink/core/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ScreenRecord } from '../extension-types.js';
import { recordTitle } from '../lib/format.js';
import { fieldOf, findEntity, listColumns, useAppMeta, type AppEntity } from '../lib/meta.js';
import { errorMessage, trpc, type RouterInputs } from '../lib/trpc.js';
import { FieldValue } from './fields.js';
import { RecordTable } from './record-table.js';
import { buttonClass, EmptyState, ErrorBanner, Spinner } from './ui.js';

export type ListQuery = Omit<RouterInputs['records']['list'], 'entity'>;

/** Records of one entity, as the reader may see them. The hook behind lists, cards and client screens. */
export function useRecords(entity: string, query: ListQuery = {}) {
  return useQuery(trpc.records.list.queryOptions({ entity, ...query }));
}

/** Run a saved report by its key, with the reader's permissions: for totals on a card. */
export function useReport(key: string) {
  return useQuery(trpc.reports.run.queryOptions({ key }));
}

/**
 * A compact list for cards: each record's title with a few fields beside it. `fields` names
 * what to show under the title; the first `aside` field sits on the right (an amount, a date).
 */
export function CompactRecordList({
  entity: entityId,
  filter,
  sort,
  stateCategory,
  limit = 5,
  fields = [],
  aside,
  empty,
}: {
  entity: string;
  filter?: Condition[];
  sort?: { field: string; dir: 'asc' | 'desc' };
  stateCategory?: ('open' | 'done' | 'canceled')[];
  limit?: number;
  fields?: string[];
  aside?: string;
  empty?: string;
}) {
  const meta = useAppMeta();
  const entity = findEntity(meta, entityId);
  const list = useRecords(entityId, {
    limit,
    ...(filter && { filter }),
    ...(sort && { sort }),
    ...(stateCategory && { state_category: stateCategory }),
  });
  if (!entity) return null;
  if (list.isLoading) return <Spinner />;
  if (list.error) return <ErrorBanner>{errorMessage(list.error)}</ErrorBanner>;
  const records = (list.data?.records ?? []) as ScreenRecord[];
  if (records.length === 0)
    return <EmptyState title={empty ?? `No ${entity.plural.toLowerCase()}`} />;
  return (
    <div>
      <ul className="divide-y divide-slate-100">
        {records.map((record) => (
          <CompactRow
            key={record.id}
            entity={entity}
            record={record}
            titles={list.data?.titles ?? {}}
            fields={fields}
            {...(aside !== undefined && { aside })}
          />
        ))}
      </ul>
      {(list.data?.total ?? 0) > records.length && (
        <p className="pt-2 text-xs text-slate-500">
          {records.length} of {list.data?.total}.{' '}
          <Link
            to="/e/$entity"
            params={{ entity: entityId }}
            className="text-brand-strong hover:underline"
          >
            See all
          </Link>
        </p>
      )}
    </div>
  );
}

function CompactRow({
  entity,
  record,
  titles,
  fields,
  aside,
}: {
  entity: AppEntity;
  record: ScreenRecord;
  titles: Record<string, string>;
  fields: string[];
  aside?: string;
}) {
  const asideField = aside ? fieldOf(entity, aside) : undefined;
  return (
    <li className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <Link
          to="/e/$entity/$id"
          params={{ entity: entity.id, id: record.id }}
          className="block truncate text-sm font-medium text-slate-800 hover:text-brand-strong hover:underline"
        >
          {recordTitle(entity, record)}
        </Link>
        {fields.length > 0 && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
            {fields.map((key) => {
              const field = fieldOf(entity, key);
              return field ? (
                <span key={key} className="inline-flex items-center gap-1">
                  <FieldValue entity={entity} field={field} record={record} titles={titles} />
                </span>
              ) : null;
            })}
          </div>
        )}
      </div>
      {asideField && (
        <div className="shrink-0 text-right text-sm text-slate-700">
          <FieldValue entity={entity} field={asideField} record={record} titles={titles} />
        </div>
      )}
    </li>
  );
}

/**
 * A full table of records matching a query, with the entity's list columns or the ones named.
 * For client tabs and screens: "this opportunity's surveys".
 */
export function RecordList({
  entity: entityId,
  query = {},
  columns,
  empty,
}: {
  entity: string;
  query?: ListQuery;
  columns?: string[];
  empty?: string;
}) {
  const meta = useAppMeta();
  const entity = findEntity(meta, entityId);
  const list = useRecords(entityId, query);
  if (!entity) return null;
  if (list.isLoading) return <Spinner />;
  if (list.error) return <ErrorBanner>{errorMessage(list.error)}</ErrorBanner>;
  return (
    <RecordTable
      entity={entity}
      records={(list.data?.records ?? []) as ScreenRecord[]}
      titles={list.data?.titles ?? {}}
      columns={listColumns(meta, entity, columns ?? null)}
      {...(empty !== undefined && { empty })}
    />
  );
}

/** A link to the create screen with values filled in, such as the opportunity a survey is for. Hidden from people who cannot create. */
export function NewRecordButton({
  entity: entityId,
  defaults,
  children,
  variant = 'primary',
}: {
  entity: string;
  defaults?: Record<string, string | number | boolean>;
  children?: ReactNode;
  variant?: 'primary' | 'secondary';
}) {
  const meta = useAppMeta();
  const entity = findEntity(meta, entityId);
  if (!entity?.access.create) return null;
  return (
    <Link
      to="/e/$entity/new"
      params={{ entity: entityId }}
      search={defaults ? { defaults } : {}}
      className={buttonClass(variant)}
    >
      <Plus className="size-4" aria-hidden /> {children ?? `New ${entity.label.toLowerCase()}`}
    </Link>
  );
}
